import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  alpha, AppBar, Toolbar, IconButton, Menu, MenuItem, Button, Box, ListItemIcon, Divider, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Tabs, Tab, LinearProgress, Chip
} from '@mui/material';
import { makeStyles, withStyles } from '@mui/styles';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BusinessIcon from '@mui/icons-material/Business';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import SyncIcon from '@mui/icons-material/Sync';
import GetAppIcon from '@mui/icons-material/GetApp';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlurLinearIcon from '@mui/icons-material/BlurLinear';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { NestedMenuItem } from 'mui-nested-menu';
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link, RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PatientService } from '../../services/patient.service';
import atman_logo from '../../assets/atman_logo.svg';
import MultiProgramInfoHeaderComponent from '../patients/detail/info-header/multi-program-info-header.component'
import SubHeaderComponent from '../patients/detail/info-header/sub-header.component'
import { RootState, useAppThunkDispatch } from '../../redux/store';
import { useCurrentUser } from '../../utils/use-current-user';
import ConfirmationDialogComponent from '../patients/detail/common/confirmation-dialog.component';
import GeneratedNotesDialogComponent from '../patients/detail/common/generated-notes-dialog.component';
import GenerateNoteOptionsDialogComponent from '../patients/detail/common/generate-note-options-dialog.component';
import Helper from '../../utils/helper';
import { checkCondition } from '../patients/detail/common/condition-check';
import stylesheet from '../patients/detail/detail-container.stylesheet'
import { setProgramChips } from '../../redux/common-data-slice';
import AddProgramDialogComponent from '../patients/detail/info-header/add-program.component';
import { useUpdateDataAction } from '../../utils/update-details-hook';
import CcdUploadsComponent from '../patients/detail/info-header/ccd-uploads.component';
import ReportDownloadComponent from '../patients/detail/info-header/report-download.component'
import OktaLogoutComponent from './okta-logout.component';
import KbStatusIndicator from './kb-status-indicator.component';
import AddAppointmentsComponent from '../patients/detail/info-header/add-appointments.component';
import moment from 'moment';
import { environment } from '../../environments/environment';
import OidcLogoutComponent from './oidc-logout.component';
import ExportPatientsDialogComponent from '../patients/detail/info-header/export-patients-dialog.component';
import { scribeService } from '../../services/scribe.service';
import { agentEvalService } from '../../services/agent-eval.service';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Badge from '@mui/material/Badge';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useThemeMode } from '../../contexts/ThemeContext';
import { backgroundJobService, ActiveJobs } from '../../services/background-job.service';
import { BackgroundJobsDialog } from '../background-jobs/BackgroundJobsDialog';

// Scratch-pad entry categories (mirrors phr.patient_scratch_pad / the
// backend SCRATCH_PAD_CATEGORIES tuple). `group` drives how the kebab-menu
// "View scratch pad" dialog buckets entries; an unknown category falls
// through to the clinical-mentions bucket rather than being dropped.
const SCRATCH_PAD_CATEGORY_META: Record<string, { label: string; group: 'medication' | 'mention' }> = {
  unresolved_medication: { label: 'Unresolved medication', group: 'medication' },
  ambiguous_medication: { label: 'Ambiguous medication', group: 'medication' },
  dose_form_assumed: { label: 'Dose form assumed', group: 'medication' },
  clinical_mention: { label: 'Clinical mention', group: 'mention' },
  patient_concern: { label: 'Patient concern', group: 'mention' },
  enhanced_question: { label: 'Sharpened questions', group: 'mention' },
}

interface IProps extends RouteComponentProps {
  openDrawerHandler: Function;
  history: any;
  location: any;
  match: any;
  isOktaEnabled: any;
}

const styles = makeStyles(() => ({
  grow: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: 2,
  },
  search: {
    position: 'relative',
    borderRadius: '4px',
    backgroundColor: alpha('#fff', 0.15),
    '&:hover': {
      backgroundColor: alpha('#fff', 0.25),
    },
    marginRight: 2,
    marginLeft: 0,
    width: '100%',
  },
  searchIcon: {
    width: 7,
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: 1,
    width: '100%',
  },
  sectionDesktop: {
    display: 'none',
  },
  sectionMobile: {
    display: 'flex',
  },
  iconCommonStyle: {
    opacity: '80%',
    color: '#ffffff',
  },
  profileIcon: {
    width: '30px',
    height: '30px',
    opacity: '80%',
    color: '#ffffff',
  },
  navLink: {
    borderRadius: '25px',
    padding: '6px 24px 6px 21px',
    width: '44px',
    height: '22px',
    background: 'var(--color-paper)',
    opacity: '70%',
    display: 'inline-block',
    fontSize: '18px',
    color: 'var(--color-text-primary)',
    textDecoration: 'none solid rgb(0, 0, 0)',
    textAlign: 'center',
  },
  activeLink: {
    background: '#f5842e',
    cursor: 'pointer',
    fontSize: '18px',
    height: '30px',
    color: 'white',
  },
  appLogo: {
    height: '45px',
    width: 'auto',
    // [theme.breakpoints.up('md')]: {
    //   height: '45px',
    //   width: 'auto',
    // },
    // [theme.breakpoints.down('sm')]: {
    //   height: '45px',
    //   width: '140px',
    // },
  },
  addPrgIcon: {
    fill: 'orange',
    width: '20px',
    height: '20px'
  },
  inactiveLink: {
    background: '#000000',
    cursor: 'pointer',
    color: 'white',
    fontSize: '16px',
    height: '30px',
  },
  linkText: {
    textDecoration: 'none',
  },
  hideSearch: {
    display: 'none',
  },
  menuItem: {
    margin: '0.25rem 0rem !important',
    padding: '0.1rem 0.8rem 0.1rem 0.25rem !important',
    fontSize: '0.7rem !important',
    '&.MuiMenuItem-root, & .MuiListItemIcon-root': {
      paddingLeft: '0.3rem !important',
    },
    '&:hover': {
      color: 'var(--color-primary) !important',
      '& .MuiListItemIcon-root': {
        color: 'var(--color-primary) !important',
      }
    },
  },
  operationsDivider: {
    // margin: '0rem 0.3rem !important',
    margin: '0rem 0.3rem 0rem 0.3rem !important',
  },
  patientOperationsDivider: {
    margin: '0rem 0.3rem !important',
    padding: '0.125rem 0.2rem !important',
  },
  logoutBtn: {
    color: '#ffffff !important',
    padding: '0rem !important',
    fontSize: '0.7rem !important',
    letterSpacing: 0,
    whiteSpace: 'nowrap',
    '&:hover': {
      color: 'var(--color-primary) !important',
    },
  },
}));

/**
 * Strip LLM thinking traces from summary text.
 * Handles <think>...</think>, <thinking>...</thinking>, and markdown code block patterns.
 */
const stripThinkingTrace = (text: string | null | undefined): string => {
  if (!text) return '';

  let cleaned = text;

  // Strip <think>...</think> or <thinking>...</thinking> tags
  if (cleaned.includes('</think>')) {
    const parts = cleaned.split('</think>');
    cleaned = parts[parts.length - 1]?.trim() || '';
  } else if (cleaned.includes('</thinking>')) {
    const parts = cleaned.split('</thinking>');
    cleaned = parts[parts.length - 1]?.trim() || '';
  }

  // If there's a markdown code block with medical content, extract just that
  const medicalMarkers = ['# History of Present Illness', '# HPI', '# Past Medical History', '# Assessment and Plan'];
  if (cleaned.includes('```markdown') || cleaned.includes('```\n#')) {
    const codeBlockMatch = cleaned.match(/```(?:markdown)?\s*([\s\S]*?)```/g);
    if (codeBlockMatch) {
      for (const block of codeBlockMatch) {
        const content = block.replace(/```(?:markdown)?/g, '').trim();
        if (medicalMarkers.some(marker => content.includes(marker))) {
          cleaned = content;
          break;
        }
      }
    }
  }

  // Strip any remaining LLM special tokens
  cleaned = cleaned.replace(/<\|im_start\|>/g, '').replace(/<\|im_end\|>/g, '');
  cleaned = cleaned.replace(/<s>/g, '').replace(/<\/s>/g, '');

  return cleaned.trim();
};

const ToolbarComponent: React.FC<IProps> = ({ openDrawerHandler, isOktaEnabled }) => {
  const [mobileAnchorEl, setMobileAnchorEl] = useState < null | HTMLElement > (null)
  const [operationAnchor, setOperationAnchor] = useState < HTMLElement | null > (null);
  const [subAnchorEl, setSubAnchorEl] = useState < HTMLElement | null > (null);
  const [adminAnchorEl, setAdminAnchorEl] = useState < HTMLElement | null > (null);
  const closeTimeoutRef = useRef < ReturnType < typeof setTimeout > | null > (null);
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [syncOpen, setSyncOpen] = useState(false)
  const [dailyAssignment, setDailyAssignment] = useState(false)
  const [sigsCleanup, setSigsCleanup] = useState(false)
  const [confirmNoteOpen, setconfirmNoteOpen] = useState(false)
  const [isExtractDiagnosis, setIsExtractDiagnosis] = useState(false)
  const loggedInUser = useSelector((state: RootState) => state.commonData.loggedInUser)
  const { currentRole } = useCurrentUser()
  const { mode, toggleMode } = useThemeMode()
  const patientService = new PatientService();
  const url = useLocation();
  const dispatch = useAppThunkDispatch()
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const selectedProgram = useSelector((state: RootState) => state.commonData.selectedProgram)
  const programsSet = useSelector((state: RootState) => state.metaData.metadata?.PROGRAMS)
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)
  const [showAppTitle,] = useState(true);
  const [visitNote, setvisitNote] = useState(null)
  const [isCloseoutNote, setIsCloseoutNote] = useState(false)
  const [isAddPrg, setIsAddPrg] = useState(false)
  const [isUploadFiles, setIsUploadFiles] = useState(false)
  const [isAddPatients, setIsAddPatients] = useState(false)
  // Parallel state for the Healthy Heart submenu variants. Keeping separate
  // booleans (rather than a shared "hospital" state) preserves the existing
  // dialog-trigger pattern and avoids branching inside the Optum handlers.
  const [isAddPatientsHH, setIsAddPatientsHH] = useState(false)
  const [isAddPatientsCC, setIsAddPatientsCC] = useState(false)
  const [isTxtReportHH, setIsTxtReportHH] = useState(false)
  const [isTxtReportCC, setIsTxtReportCC] = useState(false)
  const [isAddAppointmentHH, setIsAddAppointmentHH] = useState(false)
  const [isDownloadTaskReport, setIsDownloadTaskReport] = useState(false)
  const [isAddAppointment, setisAddAppointment] = useState(false)
  const [isAddSpreadsheets, setIsAddSpreadsheets] = useState(false)
  const [isTxtReport, setIsTxtReport] = useState(false)
  const [isImportTasks, setIsImportTasks] = useState(false)
  const [isExportPatients, setIsExportPatients] = useState(false)
  const [isNoteOptionsOpen, setIsNoteOptionsOpen] = useState(false)
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
  const [transcriptData, setTranscriptData] = useState<any>(null)
  const [transcriptLoading, setTranscriptLoading] = useState(false)
  const [transcriptTab, setTranscriptTab] = useState(0)
  const [jobsDialogOpen, setJobsDialogOpen] = useState(false)
  const [activeJobs, setActiveJobs] = useState<ActiveJobs | null>(null)
  // Scratch pad — clinical items the consumer intake flow flagged for
  // review (unresolved/assumed meds, free-form clinical mentions). The
  // open count feeds the kebab-trigger badge so the provider sees there
  // is consumer-intake material to review without opening the menu.
  const [isScratchPadOpen, setIsScratchPadOpen] = useState(false)
  const [scratchPadEntries, setScratchPadEntries] = useState<any[]>([])
  const [scratchPadLoading, setScratchPadLoading] = useState(false)
  const [scratchPadOpenCount, setScratchPadOpenCount] = useState(0)

  // Controlled polling — only active when jobs are running or dialog is open
  const stopPollingRef = useRef<(() => void) | null>(null)
  const jobsDialogOpenRef = useRef(false)

  const startPolling = useCallback(() => {
    if (stopPollingRef.current) return  // already polling
    stopPollingRef.current = backgroundJobService.pollActive((data) => {
      setActiveJobs(data)
      // Auto-stop only when no active jobs AND dialog is closed
      if (data.running.length === 0 && data.queued.length === 0 && !jobsDialogOpenRef.current) {
        stopPollingRef.current?.()
        stopPollingRef.current = null
      }
    }, 5000)
  }, [])

  // One-time fetch on mount — show badge if jobs are active from a previous session.
  //
  // Skipped under AIDA: the AIDA image excludes atman/background_jobs, so the
  // blueprint that serves /jobs/active is never registered and the route 404s —
  // the same condition as CELERY_ENABLED=false, but permanent. The browser
  // surfaces the OPTIONS preflight 404 to axios as ERR_NETWORK (no response
  // object), which latches the interceptor and strips the auth header off the
  // next request, producing a spurious 401 -> performLogout(). See
  // utils/httpInterceptor.tsx. Not requesting at all is the fix; the interceptor's
  // latch-release only limits the blast radius.
  useEffect(() => {
    if (!Helper.isAIDA()) {
      backgroundJobService.getActiveJobs().then(data => {
        setActiveJobs(data)
        if (data.running.length > 0 || data.queued.length > 0) {
          startPolling()
        }
      }).catch(() => {})
    }
    return () => { stopPollingRef.current?.() }
  }, [startPolling])

  const jobCount = (activeJobs?.running?.length || 0) + (activeJobs?.queued?.length || 0)

  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)
  const history = useHistory()

  const classes = styles()
  const handleConfirmClose = () => setConfirmOpen(false)
  const handleConfirmSyncClose = () => setSyncOpen(false)

  useEffect(() => {
    localStorage.setItem("SMS_Report", '');
  }, [])

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);


  const isPatientDetailsPage = () => {
    return (url.pathname.endsWith('/details')
      //  ||  (url.pathname.endsWith('/patient-list') && selectedPatient)
    )
  }

  // The Background Jobs button belongs only on the patient-list page —
  // never on the patient profile (details) page or anywhere else.
  const isPatientListPage = () => {
    return url.pathname === '/patient-list'
  }

  // One-time fetch when a patient page loads — populates the kebab-trigger
  // badge count. Non-blocking: a failure just leaves the badge at zero.
  useEffect(() => {
    if (!isPatientDetailsPage() || !patientDetail?.ref_id) {
      setScratchPadEntries([])
      setScratchPadOpenCount(0)
      return
    }
    patientService.getScratchPadEntries(patientDetail.ref_id)
      .then((res: any) => {
        setScratchPadEntries(res?.data?.entries || [])
        setScratchPadOpenCount(res?.data?.open_count || 0)
      })
      .catch(() => { /* badge stays at 0 — surfacing is best-effort */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetail?.ref_id, url.pathname])

  const handleConfirm = () => {
    handleMobileMenuClose();
    setConfirmOpen(true)
  }

  const handleConfirmNoteClose = () => {
    setconfirmNoteOpen(false)
    setvisitNote(null)
    setIsCloseoutNote(false)
  }

  const confirmStatus = () => {
    const payload = {
      programs: patientDetail.program_enrollments.map((e: any) => e.program).toString()
    }
    patientService.resetPatient(patientDetail.id, payload).then((res: any) => {
      setConfirmOpen(false)
      const newRefId = res?.data?.ref_id || ''
      const newURL = (window.location.href)?.replace(patientDetail.id, newRefId);
      (newRefId !== '' && newRefId !== patientDetail.id)
        ? window.location.replace(newURL)
        : updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, isProfileChange: true })
    }).catch(() => {
      setConfirmOpen(false)
      toast.error('Failed to reset patient.', Helper.bottom_center())
    })
  }

  const handleOperationMenuClose = () => {
    setOperationAnchor(null);
    setSubAnchorEl(null);
    setAdminAnchorEl(null);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Optum submenu handlers
  const handleOptumMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    // Clear any existing timeout immediately
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Close admin menu if open
    setAdminAnchorEl(null);
    // Open optum menu
    setSubAnchorEl(event.currentTarget);
  };

  const closeOptumMenu = () => {
    // Clear any existing timeout first
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    // Set new timeout
    closeTimeoutRef.current = setTimeout(() => {
      setSubAnchorEl(null);
      closeTimeoutRef.current = null;
    }, 150);
  };

  const cancelOptumClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Admin submenu handlers
  const handleAdminMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    // Clear any existing timeout immediately
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Close optum menu if open
    setSubAnchorEl(null);
    // Open admin menu
    setAdminAnchorEl(event.currentTarget);
  };

  const closeAdminMenu = () => {
    // Clear any existing timeout first
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    // Set new timeout
    closeTimeoutRef.current = setTimeout(() => {
      setAdminAnchorEl(null);
      closeTimeoutRef.current = null;
    }, 150);
  };

  const cancelAdminClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);


  const handleOperationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    operationAnchor === null && setOperationAnchor(event.currentTarget);
  }

  const handleMobileMenuClose = () => {
    setMobileAnchorEl(null)
    setSubAnchorEl(null)

    handleOperationMenuClose()
  }

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setMobileAnchorEl(event.currentTarget);

  const onExport = () => {
    handleMobileMenuClose()
    patientService.exportPatient(patientDetail.id).then((res: any) => {
      const exportUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = exportUrl;
      link.setAttribute('download', `${patientDetail.id}.json`);
      document.body.appendChild(link);
      link.click();
      toast.success(`Patient exported successfully.`, Helper.bottom_center())
    }).catch((error) => {
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const isDisabledSync = () => {
    if (patientDetail?.source_details?.length > 0)
      return patientDetail?.source_details[0][0] === null ? false : true
    return false
  }

  const checkSouceDeatils = () => {
    if (patientDetail?.source_details?.length > 0) {
      const srcDetails = patientDetail?.source_details[0] || []
      if (srcDetails?.length > 0)
        return ['EPIC', 'ELATION']?.includes(srcDetails[0])
    }
  }

  const confirmSyncStatus = () => {
    const srcDetails = patientDetail?.source_details[0] || []
    if (srcDetails?.length > 0) {
      const type = srcDetails[0]?.toUpperCase()
      const mrn = srcDetails[1]

      const prgList = patientDetail?.program_enrollments?.map((i: any) => i?.program)?.toString()
      if (type === 'EPIC') {
        patientService.syncEPICPatients(mrn, prgList).then((res: any) => {
          setSyncOpen(false)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, isProfileChange: true })

        }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
      } else if (type === 'ELATION') {
        patientService.syncELATIONPatients(mrn, prgList).then((res: any) => {
          setSyncOpen(false)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true, isProfileChange: true })

        }).catch((error) => {
          setSyncOpen(false)
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
      }
    }
  }

  const confirmSigsCLeanup = () => {
    patientService.singsCleanup(patientDetail.mrns[0]).then((res: any) => {
      setSigsCleanup(false);
      (res.data.status === 'completed')
        ? toast.success(res.data?.message || 'Data updated successfully.', Helper.bottom_center())
        : toast.error(res.data?.message || "Error while cleaning sigs on elation", Helper.bottom_center())
    }).catch((error) => {
      setSigsCleanup(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const handleConfirmSigsClose = () => setSigsCleanup(false)
  const handleConfirmAssignmentClose = () => setDailyAssignment(false)

  const onGenerateNote = () => {
    handleMobileMenuClose()
    setIsNoteOptionsOpen(true)
  }

  const handleGenerateNoteWithOptions = (includeOnboardingHeader: boolean, interweaveScribe: boolean) => {
    setIsNoteOptionsOpen(false)
    setvisitNote(null)
    setconfirmNoteOpen(true)
    setIsCloseoutNote(false)
    patientService.generateNote(patientDetail.id, includeOnboardingHeader, interweaveScribe).then((res: any) => {
      setvisitNote(res.data.generated_note_text)
      toast.success(`Patient note is generated`, Helper.bottom_center())
    }).catch((error) => {
      setvisitNote(null)
      setconfirmNoteOpen(false)
      setIsCloseoutNote(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const handleCloseOutNote = (prg: any) => {
    setconfirmNoteOpen(true)
    setIsCloseoutNote(true)
    setvisitNote(null)
    prg && patientService.closeoutNote(patientDetail.id, { program: prg }).then((res: any) => {
      if (res?.data?.generated_note_text) {
        toast.success(res?.data?.message || `Patient note closeout successfully`, Helper.bottom_center())
        setvisitNote(res?.data?.generated_note_text)
      }
    }).catch((error) => {
      setconfirmNoteOpen(false)
      setvisitNote(null)
      setIsCloseoutNote(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const onWriteNote = (visitNoteEditedText: any) => {
    const payload = { 'visit_note_text': visitNoteEditedText }
    patientService.submitNote(patientDetail.id, payload).then((res: any) => {
      toast.success(`Patient note is created on Elation`, Helper.bottom_center())
      setconfirmNoteOpen(false)
      setvisitNote(null)
      setIsCloseoutNote(false)
    }).catch((error) => {
      setconfirmNoteOpen(false)
      setvisitNote(null)
      setIsCloseoutNote(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const redirectToHome = () => {
    dispatch(setProgramChips([]))
    history.push('/');
  }

  const menuId = 'primary-search-account-menu';

  const handleAddProgram = () => {
    handleMobileMenuClose()
    setIsAddPrg(true)
  }

  const handleExtractDiagnosis = () => {
    handleMobileMenuClose()
    setIsExtractDiagnosis(true)
  }

  const handleUploadFilesClose = () => {
    isTxtReport
      ? setIsTxtReport(false)
      : isTxtReportHH
        ? setIsTxtReportHH(false)
        : isTxtReportCC
          ? setIsTxtReportCC(false)
          : isAddSpreadsheets
            ? setIsAddSpreadsheets(false)
            : isAddPatients
              ? setIsAddPatients(false)
              : isAddPatientsHH
                ? setIsAddPatientsHH(false)
                : isAddPatientsCC
                  ? setIsAddPatientsCC(false)
                  : isImportTasks
                    ? setIsImportTasks(false)
                    : isDownloadTaskReport
                      ? setIsDownloadTaskReport(false)
                      : setIsUploadFiles(false)
  }

  const handleUploadFiles = () => {
    handleMobileMenuClose()
    setIsUploadFiles(true)
  }

  const handleAddPatients = () => {
    handleMobileMenuClose()
    setIsAddPatients(true)
  }

  const handleAssignTask = () => {
    handleOperationMenuClose()
    handleConfirmAssignmentClose()
    patientService.assignTask().then((res: any) => {
      const status = res?.data?.status
      if (status === 'QUEUED') {
        toast.info('Daily assignment queued \u2014 another job is running', Helper.bottom_center())
      } else {
        toast.success('Daily assignment started', Helper.bottom_center())
      }
      startPolling()
    }).catch((error: any) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const handleImportSFTasks = () => {
    handleMobileMenuClose()
    setIsImportTasks(true)
  }

  const handleTxtReport = () => {
    localStorage.setItem("SMS_Report", '')
    handleMobileMenuClose()
    setIsTxtReport(true)
  }

  const handleAddAppointment = () => {
    handleMobileMenuClose()
    setisAddAppointment(true)
  }

  // Healthy Heart variants — parallel handlers that reuse the same dialogs
  // but trigger the HH state booleans so the dialog can render with the
  // HH-specific title (which in turn is the discriminator the dialog uses
  // to pick the correct backend endpoint / hospital_tag).
  const handleAddPatientsHH = () => {
    handleMobileMenuClose()
    setIsAddPatientsHH(true)
  }

  // ChamberCardio variant — standalone client tag, same spreadsheet dialog.
  const handleAddPatientsCC = () => {
    handleMobileMenuClose()
    setIsAddPatientsCC(true)
  }

  const handleAddAppointmentHH = () => {
    handleMobileMenuClose()
    setIsAddAppointmentHH(true)
  }

  const handleTxtReportHH = () => {
    localStorage.setItem("SMS_Report", '')
    handleMobileMenuClose()
    setIsTxtReportHH(true)
  }

  const handleTxtReportCC = () => {
    localStorage.setItem("SMS_Report", '')
    handleMobileMenuClose()
    setIsTxtReportCC(true)
  }

  const handleGenerateTaskReportUpdated = () => {
    handleMobileMenuClose()
    setIsDownloadTaskReport(true)
  }

  const handleCloseAddAppointment = () => setisAddAppointment(false)

  const handleCloseAddAppointmentHH = () => setIsAddAppointmentHH(false)

  const handleCloseDownloadTaskReport = () => setIsDownloadTaskReport(false)

  const getPrgList = () => {
    const prgList = programsSet?.map((item: any) => {
      return (selectedProgram.includes(item.name))
        ? { ...item, checked: true }
        : { ...item }
    }) || []

    return [...prgList]?.sort((a: any, b: any) => a.display_name > b.display_name ? 1 : -1)
  }
  const handlePrgDialogClose = () => setIsAddPrg(false)

  const handlePrgDialogSubmit = (data: any) => {
    handlePrgDialogClose()
    data && patientService.createEnrollment(patientDetail.id, { 'program_name': data })
      .then((_res: any) => {
        toast.success('Program added successfully', Helper.bottom_center())
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      }).catch((error) => {
        setConfirmOpen(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
  }

  const handleGenerateExtract = (hospital: any) => {
    handleMobileMenuClose()
    hospital = hospital ? hospital : 'optum'
    patientService.generateExports(hospital).then((res: any) => {
      const status = res?.data?.status
      if (status === 'QUEUED') {
        toast.info('Report generation queued \u2014 another job is running', Helper.bottom_center())
      } else {
        toast.success('Report generation started.', Helper.bottom_center())
      }
      startPolling()
    }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const handleGenerateDashboard = (hospital: any, isDownload = false) => {
    handleMobileMenuClose()
    hospital = hospital ? hospital : 'optum'
    if (isDownload) {
      patientService.getDashboardReportStatus(hospital).then((res: any) => {
        if (res?.data?.message === "Report generated") {
          patientService.extractDashboardReport(hospital).then((res: any) => {
            Helper.handleReportFile(res?.data, 'dashboard_report')
          }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
        } else
          toast.warning(res?.data?.message || 'Report generation not completed.', Helper.bottom_center())
      })
    } else {
      patientService.generateDashboardReport(hospital).then((res: any) => {
        const status = res?.data?.status
        if (status === 'QUEUED') {
          toast.info('Dashboard report queued \u2014 another job is running', Helper.bottom_center())
        } else {
          toast.success('Dashboard report generation started.', Helper.bottom_center())
        }
        startPolling()
      }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
    }
  }


  const handleTasksEventsExtract = (hospital = '') => {
    handleMobileMenuClose()
    hospital !== '' && patientService.downloadTasksEventsExport(hospital).then((res: any) => {
      res?.data && Helper.handleDashboardReportFile(res?.data, `Tasks_and_Events_Report`)
    }).catch((error) => toast.error(Helper.getError(error), Helper.bottom_center()))
  }

  const handleGenerateTasksEventsReport = (hospital = '') => {
    handleMobileMenuClose()
    hospital !== '' && patientService.generateTasksEventsReport(hospital).then((res: any) => {
      const status = res?.data?.status
      if (status === 'QUEUED') {
        toast.info('Tasks & events report queued \u2014 another job is running', Helper.bottom_center())
      } else {
        toast.success('Tasks & events report generation started.', Helper.bottom_center())
      }
      startPolling()
    }).catch((error) => toast.error(Helper.getError(error), Helper.bottom_center()))
  }

  const handleInactiveCandidatesReport = (hospital = '') => {
    handleMobileMenuClose()
    hospital !== '' && patientService.downloadInactiveCandidatesReport(hospital).then((res: any) => {
      if (res?.data) {
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(new Blob([res.data]))
        link.setAttribute('download', `Inactive_Patient_Candidates_Report_${moment().format('YYYY-MM-DD')}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
      }
    }).catch((error) => toast.error(Helper.getError(error), Helper.bottom_center()))
  }

  const handleElationImport = (hospitalTag: string, elationSource: string = 'atman') => {
    handleMobileMenuClose()
    patientService.getElationImport(elationSource, hospitalTag).then((res: any) => {
      if (res?.data?.message === 'success')
        toast.success(`Elation import started for ${hospitalTag}`, Helper.bottom_center())
    }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const handleDownloadNotes = (hospital: any) => {
    handleMobileMenuClose()
    patientService.extractHospitalNotesReport(hospital).then((res: any) => {
      Helper.handleReportFile(res?.data, 'notes_report')
    }).catch((err: any) => toast.error(Helper.getError(err), Helper.bottom_center()))
  }

  const handleExportScribeTranscripts = () => {
    handleMobileMenuClose()
    toast.info('Exporting scribe transcripts...', Helper.bottom_center())
    scribeService.exportTranscripts().then((blob: Blob) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `scribe_transcripts_${moment().format('YYYY-MM-DD')}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Scribe transcripts exported successfully.', Helper.bottom_center())
    }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
  }

  const handleExportAgentEvalSessions = () => {
    handleMobileMenuClose()
    toast.info('Exporting transcript sessions...', Helper.bottom_center())
    agentEvalService.exportSessions().then((blob: Blob) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `transcript_sessions_${moment().format('YYYY-MM-DD')}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Transcript sessions exported successfully.', Helper.bottom_center())
    }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
  }

  const handleExportAiFeedback = (type: string) => {
    handleMobileMenuClose()
    const labels: Record<string, string> = { diagnosis: 'AI diagnoses', measurement: 'AI measurements', med_sig: 'Med reconciliation' }
    toast.info(`Exporting ${labels[type] || type}...`, Helper.bottom_center())
    patientService.exportAiFeedback(type).then((blob: Blob) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `ai_feedback_${type}_${moment().format('YYYY-MM-DD')}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success(`${labels[type] || type} feedback exported.`, Helper.bottom_center())
    }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
  }

  const handleViewLastTranscript = async () => {
    handleMobileMenuClose()
    setTranscriptLoading(true)
    setIsTranscriptOpen(true)
    setTranscriptData(null)
    setTranscriptTab(0)

    try {
      // Get the list of jobs for this patient
      const jobsResponse = await scribeService.listPatientJobs(patientDetail.id, { limit: 1, status: 'completed' })

      if (!jobsResponse.jobs || jobsResponse.jobs.length === 0) {
        toast.warning('No completed transcripts found for this patient.', Helper.bottom_center())
        setIsTranscriptOpen(false)
        setTranscriptLoading(false)
        return
      }

      // Get the most recent completed job's result
      const lastJob = jobsResponse.jobs[0]
      const result = await scribeService.getJobResult(lastJob.job_id)

      setTranscriptData({
        ...result,
        created_at: lastJob.created_at,
        call_type_display: lastJob.call_type_display
      })
      setTranscriptLoading(false)
    } catch (err: any) {
      setTranscriptLoading(false)
      setIsTranscriptOpen(false)
      toast.error(Helper.getErrorText(err) || 'Failed to load transcript', Helper.bottom_center())
    }
  }

  const handleViewScratchPad = async () => {
    handleMobileMenuClose()
    setIsScratchPadOpen(true)
    setScratchPadLoading(true)
    try {
      // Refetch on open so the dialog reflects anything the consumer
      // intake added since the page loaded.
      const res: any = await patientService.getScratchPadEntries(patientDetail.ref_id)
      setScratchPadEntries(res?.data?.entries || [])
      setScratchPadOpenCount(res?.data?.open_count || 0)
    } catch (err: any) {
      toast.error(Helper.getErrorText(err) || 'Failed to load scratch pad', Helper.bottom_center())
    } finally {
      setScratchPadLoading(false)
    }
  }

  // Read-only render of one scratch-pad entry. No lifecycle actions —
  // the open -> acknowledged/resolved audit trail is deferred; an entry
  // is just one more piece of material for the clinician to review.
  const renderScratchPadEntry = (entry: any) => {
    const meta = SCRATCH_PAD_CATEGORY_META[entry?.category] || { label: entry?.category || 'Item' }
    return (
      <Box
        key={entry.id}
        sx={{
          mb: 1.5, p: 1.5, borderRadius: 1,
          border: '1px solid var(--color-border-light)',
          backgroundColor: 'var(--color-paper)',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Chip label={meta.label} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
          <Typography variant="caption" color="textSecondary">
            {entry.created_at ? moment(entry.created_at).format('MMM D, YYYY h:mm A') : ''}
          </Typography>
        </Stack>
        {entry.brief_summary && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {entry.brief_summary}
          </Typography>
        )}
        {entry.verbatim_quote && (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
            Patient said: &ldquo;{entry.verbatim_quote}&rdquo;
          </Typography>
        )}
        {/* The actual questions (e.g. the sharpened `enhanced_question` set). brief_summary
            above is only a DESCRIPTOR — the content lives in payload.questions, so without
            this the entry showed a label and nothing else. */}
        {Array.isArray(entry.payload?.questions) && entry.payload.questions.length > 0 && (
          <Stack sx={{ mt: 0.75 }} spacing={0.5}>
            {entry.payload.questions.map((q: any, i: number) =>
              (q?.enhanced || q?.verbatim) ? (
                <Typography key={i} variant="body2" sx={{ color: 'var(--color-text-primary)' }}>
                  &bull;&nbsp;{q.enhanced || q.verbatim}
                </Typography>
              ) : null
            )}
          </Stack>
        )}
      </Box>
    )
  }

  const renderScratchPadGroup = (title: string, entries: any[]) => {
    if (!entries.length) return null
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'var(--color-primary)' }}>
          {title} ({entries.length})
        </Typography>
        {entries.map(renderScratchPadEntry)}
      </Box>
    )
  }

  const handleDownloadExtract = (hospital: any) => {
    handleMobileMenuClose()
    if (hospital === 'spreadsheet') {
      patientService.extractSpreadsheet().then((res: any) => {
        Helper.handleDashboardReportFile(res?.data, `patient_tracking`)
      }).catch((err: any) => {
        err.response?.data?.text().then((err_payload: any) => {
          toast.error(Helper.getError(JSON.parse(err_payload)), Helper.bottom_center())
        }).catch(() => {
          toast.error(Helper.getErrorText(JSON.parse(err)), Helper.bottom_center())
        })
      })
    } else if (hospital === 'northwestern_university_clinic') {
      patientService.extractAIDAReport(hospital).then((res: any) => {
        Helper.handleReportFile(res?.data, 'stage_interaction_report')
      }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
    } else if (hospital === 'sf_task') {
      patientService.extractMigrationReport('optum').then((res: any) => {
        if (res?.data)
          Helper.handleTaskReportFile(res?.data, `sf_migration_report`)
      }).catch((err: any) => {
        toast.error(Helper.getError(err), Helper.bottom_center())
      })
    } else {
      patientService.extractTreatmentReport(hospital).then((res: any) => {
        Helper.handleReportFile(res?.data, 'treatment_report')
      }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))

      patientService.extractHospitalInteractionReport(hospital).then((res: any) => {
        Helper.handleReportFile(res?.data, 'interaction_report')
      }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))

      patientService.extractHospitalBPReport().then((res: any) => {
        Helper.handleReportZipFile(res?.data, `bp_report_${moment().unix()}`)
      }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))

      patientService.extractHospitalRXReport(hospital).then((res: any) => {
        Helper.handleReportFile(res?.data, 'rx_report')
      }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))

      patientService.extractHospitalLabReport(hospital).then((res: any) => {
        Helper.handleReportFile(res?.data, 'lab_report')
      }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))

      // HealthyHeart/ChamberCardio additions to the bundle download (other
      // hospitals' report menus are deliberately unchanged): tasks & events
      // (built by the same generate-exports job; the separate HH menu item was
      // removed) and the case-rate funnel (gated by CASE_RATE_FUNNEL_HOSPITALS
      // backend-side).
      if (hospital === 'healthyheart' || hospital === 'chambercardio') {
        patientService.downloadTasksEventsExport(hospital).then((res: any) => {
          res?.data && Helper.handleDashboardReportFile(res?.data, `Tasks_and_Events_Report`)
        }).catch((err: any) => toast.error(Helper.getError(err), Helper.bottom_center()))

        patientService.downloadCaseRateFunnelReport(hospital).then((res: any) => {
          const date = moment(moment.now()).format('YYYY-MM-DD')
          res?.data && Helper.handleTaskReportFile(res?.data, `case_rate_funnel_${date}.xlsx`)
        }).catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
      }
    }
  }

  const getPatientMenu = () => {
    return (
      <>
        {!Helper.isAIDA() && (
          <MenuItem onClick={handleAddProgram} className={classes.menuItem}>
            <ListItemIcon>
              <AddBoxIcon fontSize="small" />
            </ListItemIcon>
            Add new program
          </MenuItem>
        )}
        {/* <MenuItem onClick={handleExtractDiagnosis} className={classes.menuItem}>
        <ListItemIcon><AddBoxIcon fontSize="small" /></ListItemIcon>Extract diagnosis
      </MenuItem> */}
        {!Helper.is_AIDA_OIDC() && <Divider className={classes.patientOperationsDivider} />}
        <MenuItem onClick={onGenerateNote} className={classes.menuItem}>
          <ListItemIcon>
            <BlurLinearIcon fontSize="small" />
          </ListItemIcon>
          Generate encounter note
        </MenuItem>
        {!Helper.isAIDA() && (
          <MenuItem onClick={handleViewLastTranscript} className={classes.menuItem}>
            <ListItemIcon>
              <RecordVoiceOverIcon fontSize="small" />
            </ListItemIcon>
            View last transcript
          </MenuItem>
        )}
        {!Helper.isAIDA() && (
          <MenuItem onClick={handleViewScratchPad} className={classes.menuItem}>
            <ListItemIcon>
              <TextSnippetIcon fontSize="small" />
            </ListItemIcon>
            View scratch pad
            {scratchPadOpenCount > 0 && (
              <Chip
                label={scratchPadOpenCount}
                size="small"
                sx={{
                  ml: 1, height: 16, fontSize: '0.6rem',
                  backgroundColor: 'var(--color-primary)', color: '#fff',
                }}
              />
            )}
          </MenuItem>
        )}
        {!patientDetail.is_test_patient && Helper.checkSouceDeatils(patientDetail.source_details) && (isOktaEnabled || Helper.is_AIDA_OIDC()) && (
          <>
            {/* <MenuItem onClick={handleUploadFiles} className={classes.menuItem}>
            <ListItemIcon><FileUploadIcon fontSize="small" /></ListItemIcon>CCD elation uploads
          </MenuItem> */}
            {/* <MenuItem
            onClick={() => {
              handleMobileMenuClose();
              setSigsCleanup(true)
            }}
            className={classes.menuItem}
            disabled={!isDisabledSync()}
          >
            <ListItemIcon><CloudSyncIcon fontSize="small" /></ListItemIcon>Cleanup elation sigs
          </MenuItem> */}
            <MenuItem
              onClick={() => {
                handleMobileMenuClose()
                setSyncOpen(isDisabledSync() ? true : false)
              }}
              className={classes.menuItem}
              disabled={!isDisabledSync()}
            >
              <ListItemIcon>
                <SyncIcon fontSize="small" />
              </ListItemIcon>
              Sync elation patient
            </MenuItem>
          </>
        )}
      </>
    )
  }

  const renderAIDAOperationMenu = (
    <Menu
      id={menuId}
      open={Boolean(operationAnchor)}
      anchorEl={operationAnchor}
      onClose={handleOperationMenuClose}
      MenuListProps={{ onMouseLeave: handleOperationMenuClose }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ top: '4%', left: '1%' }}
    >
      <MenuItem onClick={() => handleGenerateDashboard('northwestern_university_clinic', false)} className={classes.menuItem}>
        <ListItemIcon>
          <GroupAddIcon sx={{ fontSize: "0.9rem", paddingLeft: '0.1rem', paddingRight: '0rem' }} />
        </ListItemIcon>Generate dashboard report
      </MenuItem>
      <MenuItem onClick={() => handleGenerateDashboard('northwestern_university_clinic', true)} className={classes.menuItem}>
        <ListItemIcon><GetAppIcon sx={{ fontSize: "1rem", }} /></ListItemIcon>Download dashboard report
      </MenuItem>
      <Divider className={classes.operationsDivider} />
      <MenuItem onClick={() => handleGenerateExtract('northwestern_university_clinic')} className={classes.menuItem}>
        <ListItemIcon>
          <GroupAddIcon sx={{ fontSize: "0.9rem", paddingLeft: '0.1rem', paddingRight: '0rem' }} />
        </ListItemIcon>Generate workflow report
      </MenuItem>
      <MenuItem onClick={() => handleDownloadExtract('northwestern_university_clinic')} className={classes.menuItem}>
        <ListItemIcon><GetAppIcon sx={{ fontSize: "1rem", }} /></ListItemIcon>Download workflow report
      </MenuItem>
      <MenuItem onClick={() => handleTasksEventsExtract('northwestern_university_clinic')} className={classes.menuItem}>
        <ListItemIcon>
          <GetAppIcon sx={{ fontSize: "1rem", }} />
        </ListItemIcon>Download tasks & events report
      </MenuItem>
    </Menu>
  )

  const renderOperationMenu = (
    <Menu
      id={menuId}
      open={Boolean(operationAnchor)}
      anchorEl={operationAnchor}
      onClose={handleOperationMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{
        top: '4%',
        left: '1%',
        '& .MuiMenu-list.MuiList-padding': {
          padding: '0px !important',
        },
        '& .MuiSvgIcon-root': {
          fill: 'var(--color-text-secondary)',
        },
      }}
    >
      {/* ADMIN MENU */}
      <NestedMenuItem
        label="Admin"
        leftIcon={<SupervisorAccountIcon sx={{ padding: '0.25rem 0rem 0rem 0.3rem !important' }} />}
        parentMenuOpen={Boolean(operationAnchor)}
        sx={{
          '& .MuiMenu-list': {
            padding: '0px !important',
          },
          '& .MuiMenuItem-root': {
            minHeight: '1.2rem !important',
            alignItems: 'center !important',
            padding: '0px !important',
          },
          '& .MuiTypography-root': {
            fontSize: '0.7rem !important',
            padding: '0.3rem 0.8rem 0rem 0.9rem !important',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem !important',
            verticalAlign: 'middle',
            fill: 'var(--color-text-secondary)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleOperationMenuClose()
            setIsExportPatients(true)
          }}
          className={classes.menuItem}
        >
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Export patients
        </MenuItem>
        <MenuItem onClick={handleExportScribeTranscripts} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Export scribe transcripts
        </MenuItem>
        <MenuItem onClick={handleExportAgentEvalSessions} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Export transcript sessions
        </MenuItem>

        {!Helper.isAIDA() && (
          <MenuItem onClick={() => handleExportAiFeedback('diagnosis')} className={classes.menuItem}>
            <ListItemIcon><GetAppIcon sx={{ fontSize: "1rem" }} /></ListItemIcon>
            Export AI diagnosis feedback
          </MenuItem>
        )}

        {!Helper.isAIDA() && (
          <MenuItem onClick={() => handleExportAiFeedback('measurement')} className={classes.menuItem}>
            <ListItemIcon><GetAppIcon sx={{ fontSize: "1rem" }} /></ListItemIcon>
            Export AI measurement feedback
          </MenuItem>
        )}

        <MenuItem onClick={() => handleExportAiFeedback('med_sig')} className={classes.menuItem}>
          <ListItemIcon><GetAppIcon sx={{ fontSize: "1rem" }} /></ListItemIcon>
          Export med recon feedback
        </MenuItem>
      </NestedMenuItem>

      <Divider className={classes.operationsDivider} />

      <MenuItem onClick={() => setDailyAssignment(true)} className={classes.menuItem}>
        <ListItemIcon>
          <ListAltIcon sx={{ fontSize: '1rem' }} />
        </ListItemIcon>
        Assign daily tasks
      </MenuItem>

      <MenuItem onClick={handleGenerateTaskReportUpdated} className={classes.menuItem}>
        <ListItemIcon>
          <TextSnippetIcon sx={{ fontSize: '1rem' }} />
        </ListItemIcon>
        Download daily task report
      </MenuItem>

      <Divider className={classes.operationsDivider} />

      {/* OPTUM MENU */}
      <NestedMenuItem
        label="Optum"
        leftIcon={<BusinessIcon sx={{ fontSize: '1rem', padding: '0.25rem 0rem 0rem 0.3rem !important' }} />}
        parentMenuOpen={Boolean(operationAnchor)}
        sx={{
          '& .MuiMenu-list': {
            padding: '0px !important',
          },
          '& .MuiMenuItem-root': {
            height: '1.2rem !important',
            alignItems: 'center !important',
          },
          '& .MuiTypography-root': {
            fontSize: '0.7rem !important',
            padding: '0.3rem 0.8rem 0rem 0.9rem !important',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem !important',
            verticalAlign: 'middle',
            fill: 'var(--color-text-secondary)',
          },
        }}
      >
        {isOktaEnabled && !Helper.is_AIDA_OIDC() && (
          <>
            <MenuItem onClick={handleAddPatients} className={classes.menuItem}>
              <ListItemIcon>
                <PersonAddAlt1Icon sx={{ fontSize: '0.9rem', paddingLeft: '0.09rem' }} />
              </ListItemIcon>
              Add optum patients
            </MenuItem>

            <MenuItem onClick={handleAddAppointment} className={classes.menuItem}>
              <ListItemIcon>
                <CalendarMonthTwoToneIcon sx={{ fontSize: '0.88rem', paddingLeft: '0.05rem' }} />
              </ListItemIcon>
              Add elation appointments for optum
            </MenuItem>
          </>
        )}

        <MenuItem onClick={handleTxtReport} className={classes.menuItem}>
          <ListItemIcon>
            <TextSnippetIcon sx={{ fontSize: '0.85rem', paddingLeft: '0.08rem' }} />
          </ListItemIcon>
          Generate SMS report {localStorage.getItem('SMS_Report')}
          {localStorage.getItem('SMS_Report') === 'started' && <CircularProgress sx={{ width: '0.8rem', height: '0.7rem', marginLeft: '0.5rem' }} />}
        </MenuItem>

        <MenuItem onClick={() => handleGenerateExtract('optum')} className={classes.menuItem}>
          <ListItemIcon>
            <GroupAddIcon sx={{ fontSize: '0.9rem', paddingLeft: '0.2rem' }} />
          </ListItemIcon>
          Generate optum reports
        </MenuItem>

        <MenuItem onClick={() => handleDownloadExtract('optum')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download optum reports
        </MenuItem>

        <MenuItem onClick={() => handleDownloadNotes('optum')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download optum patients notes
        </MenuItem>

        <MenuItem onClick={() => handleTasksEventsExtract('optum')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download tasks & events report
        </MenuItem>

        <MenuItem onClick={() => handleInactiveCandidatesReport('optum')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download inactive candidates report
        </MenuItem>
      </NestedMenuItem>

      {/* HEALTHY HEART MENU */}
      {/* Healthy Heart is a sub-hospital under the Optum Elation chain (tag-routed
          to hospital_name='healthyheart'). The submenu mirrors Optum 1:1 so that
          patient intake, appointment import, and SMS report are cleanly scoped to
          Healthy Heart patients server-side (backend filters on hospital_name /
          tag_list). Optum and Healthy Heart never mix. */}
      <NestedMenuItem
        label="Healthy Heart"
        leftIcon={<BusinessIcon sx={{ fontSize: '1rem', padding: '0.25rem 0rem 0rem 0.3rem !important' }} />}
        parentMenuOpen={Boolean(operationAnchor)}
        sx={{
          '& .MuiMenu-list': {
            padding: '0px !important',
          },
          '& .MuiMenuItem-root': {
            height: '1.2rem !important',
            alignItems: 'center !important',
          },
          '& .MuiTypography-root': {
            fontSize: '0.7rem !important',
            padding: '0.3rem 0.8rem 0rem 0.9rem !important',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem !important',
            verticalAlign: 'middle',
            fill: 'var(--color-text-secondary)',
          },
        }}
      >
        {isOktaEnabled && !Helper.is_AIDA_OIDC() && (
          <>
            <MenuItem onClick={handleAddPatientsHH} className={classes.menuItem}>
              <ListItemIcon>
                <PersonAddAlt1Icon sx={{ fontSize: '0.9rem', paddingLeft: '0.09rem' }} />
              </ListItemIcon>
              Add Healthy Heart patients
            </MenuItem>

            <MenuItem onClick={() => handleElationImport('healthyheart')} className={classes.menuItem}>
              <ListItemIcon>
                <PersonAddAlt1Icon sx={{ fontSize: '0.9rem', paddingLeft: '0.09rem' }} />
              </ListItemIcon>
              Import Healthy Heart patients from Elation
            </MenuItem>

            <MenuItem onClick={handleAddAppointmentHH} className={classes.menuItem}>
              <ListItemIcon>
                <CalendarMonthTwoToneIcon sx={{ fontSize: '0.88rem', paddingLeft: '0.05rem' }} />
              </ListItemIcon>
              Add elation appointments for Healthy Heart
            </MenuItem>
          </>
        )}

        <MenuItem onClick={handleTxtReportHH} className={classes.menuItem}>
          <ListItemIcon>
            <TextSnippetIcon sx={{ fontSize: '0.85rem', paddingLeft: '0.08rem' }} />
          </ListItemIcon>
          Generate Healthy Heart SMS report {localStorage.getItem('SMS_Report')}
          {localStorage.getItem('SMS_Report') === 'started' && <CircularProgress sx={{ width: '0.8rem', height: '0.7rem', marginLeft: '0.5rem' }} />}
        </MenuItem>

        <MenuItem onClick={() => handleGenerateExtract('healthyheart')} className={classes.menuItem}>
          <ListItemIcon>
            <GroupAddIcon sx={{ fontSize: '0.9rem', paddingLeft: '0.2rem' }} />
          </ListItemIcon>
          Generate Healthy Heart reports
        </MenuItem>

        <MenuItem onClick={() => handleDownloadExtract('healthyheart')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download Healthy Heart reports
        </MenuItem>

        <MenuItem onClick={() => handleDownloadNotes('healthyheart')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download Healthy Heart patients notes
        </MenuItem>

        <MenuItem onClick={() => handleInactiveCandidatesReport('healthyheart')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download inactive candidates report
        </MenuItem>
      </NestedMenuItem>

      {/* BOYLSTON MENU */}
      <NestedMenuItem
        label="Boylston"
        leftIcon={<BusinessIcon sx={{ fontSize: '1rem', padding: '0.25rem 0rem 0rem 0.3rem !important' }} />}
        parentMenuOpen={Boolean(operationAnchor)}
        sx={{
          '& .MuiMenu-list': { padding: '0px !important' },
          '& .MuiMenuItem-root': { height: '1.2rem !important', alignItems: 'center !important' },
          '& .MuiTypography-root': { fontSize: '0.7rem !important', padding: '0.3rem 0.8rem 0rem 0.9rem !important' },
          '& .MuiSvgIcon-root': { fontSize: '1rem !important', verticalAlign: 'middle', fill: 'var(--color-text-secondary)' },
        }}
      >
        <MenuItem onClick={() => handleElationImport('boylston')} className={classes.menuItem}>
          <ListItemIcon>
            <PersonAddAlt1Icon sx={{ fontSize: '0.9rem', paddingLeft: '0.09rem' }} />
          </ListItemIcon>
          Import Boylston patients from Elation
        </MenuItem>
        <MenuItem onClick={() => handleGenerateTasksEventsReport('boylston')} className={classes.menuItem}>
          <ListItemIcon>
            <GroupAddIcon sx={{ fontSize: '0.9rem', paddingLeft: '0.2rem' }} />
          </ListItemIcon>
          Generate tasks & events report
        </MenuItem>
        <MenuItem onClick={() => handleTasksEventsExtract('boylston')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download tasks & events report
        </MenuItem>
      </NestedMenuItem>

      {/* LIFEMD MENU */}
      <NestedMenuItem
        label="LifeMD"
        leftIcon={<BusinessIcon sx={{ fontSize: '1rem', padding: '0.25rem 0rem 0rem 0.3rem !important' }} />}
        parentMenuOpen={Boolean(operationAnchor)}
        sx={{
          '& .MuiMenu-list': { padding: '0px !important' },
          '& .MuiMenuItem-root': { height: '1.2rem !important', alignItems: 'center !important' },
          '& .MuiTypography-root': { fontSize: '0.7rem !important', padding: '0.3rem 0.8rem 0rem 0.9rem !important' },
          '& .MuiSvgIcon-root': { fontSize: '1rem !important', verticalAlign: 'middle', fill: 'var(--color-text-secondary)' },
        }}
      >
        <MenuItem onClick={() => handleElationImport('lifemd')} className={classes.menuItem}>
          <ListItemIcon>
            <PersonAddAlt1Icon sx={{ fontSize: '0.9rem', paddingLeft: '0.09rem' }} />
          </ListItemIcon>
          Import LifeMD patients from Elation
        </MenuItem>
        <MenuItem onClick={() => handleGenerateTasksEventsReport('lifemd')} className={classes.menuItem}>
          <ListItemIcon>
            <GroupAddIcon sx={{ fontSize: '0.9rem', paddingLeft: '0.2rem' }} />
          </ListItemIcon>
          Generate tasks & events report
        </MenuItem>
        <MenuItem onClick={() => handleTasksEventsExtract('lifemd')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download tasks & events report
        </MenuItem>
      </NestedMenuItem>

      {/* CHAMBERCARDIO MENU */}
      {/* ChamberCardio is a standalone client tag in Atman's Elation practice
          (tag-routed to hospital_name='chambercardio'). Mirrors the Healthy
          Heart submenu minus the SMS report and appointment import; the
          reports bundle includes tasks & events and the case-rate funnel. */}
      <NestedMenuItem
        label="ChamberCardio"
        leftIcon={<BusinessIcon sx={{ fontSize: '1rem', padding: '0.25rem 0rem 0rem 0.3rem !important' }} />}
        parentMenuOpen={Boolean(operationAnchor)}
        sx={{
          '& .MuiMenu-list': { padding: '0px !important' },
          '& .MuiMenuItem-root': { height: '1.2rem !important', alignItems: 'center !important' },
          '& .MuiTypography-root': { fontSize: '0.7rem !important', padding: '0.3rem 0.8rem 0rem 0.9rem !important' },
          '& .MuiSvgIcon-root': { fontSize: '1rem !important', verticalAlign: 'middle', fill: 'var(--color-text-secondary)' },
        }}
      >
        {isOktaEnabled && !Helper.is_AIDA_OIDC() && (
          <>
            <MenuItem onClick={handleAddPatientsCC} className={classes.menuItem}>
              <ListItemIcon>
                <PersonAddAlt1Icon sx={{ fontSize: '0.9rem', paddingLeft: '0.09rem' }} />
              </ListItemIcon>
              Add ChamberCardio patients
            </MenuItem>

            <MenuItem onClick={() => handleElationImport('chambercardio')} className={classes.menuItem}>
              <ListItemIcon>
                <PersonAddAlt1Icon sx={{ fontSize: '0.9rem', paddingLeft: '0.09rem' }} />
              </ListItemIcon>
              Import ChamberCardio patients from Elation
            </MenuItem>
          </>
        )}

        <MenuItem onClick={handleTxtReportCC} className={classes.menuItem}>
          <ListItemIcon>
            <TextSnippetIcon sx={{ fontSize: '0.85rem', paddingLeft: '0.08rem' }} />
          </ListItemIcon>
          Generate ChamberCardio SMS report {localStorage.getItem('SMS_Report')}
          {localStorage.getItem('SMS_Report') === 'started' && <CircularProgress sx={{ width: '0.8rem', height: '0.7rem', marginLeft: '0.5rem' }} />}
        </MenuItem>

        <MenuItem onClick={() => handleGenerateExtract('chambercardio')} className={classes.menuItem}>
          <ListItemIcon>
            <GroupAddIcon sx={{ fontSize: '0.9rem', paddingLeft: '0.2rem' }} />
          </ListItemIcon>
          Generate ChamberCardio reports
        </MenuItem>

        <MenuItem onClick={() => handleDownloadExtract('chambercardio')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download ChamberCardio reports
        </MenuItem>

        <MenuItem onClick={() => handleDownloadNotes('chambercardio')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download ChamberCardio patients notes
        </MenuItem>

        <MenuItem onClick={() => handleInactiveCandidatesReport('chambercardio')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download inactive candidates report
        </MenuItem>
      </NestedMenuItem>

      {/* SELF-ENROLL PORTAL MENU
          Self-enrolled (consumer chat) patients are bucketed under
          hospital_name='self_enroll_portal'. Not Elation-fed, so no
          Elation import action. */}
      <NestedMenuItem
        label="Self-Enroll Portal"
        leftIcon={<BusinessIcon sx={{ fontSize: '1rem', padding: '0.25rem 0rem 0rem 0.3rem !important' }} />}
        parentMenuOpen={Boolean(operationAnchor)}
        sx={{
          '& .MuiMenu-list': { padding: '0px !important' },
          '& .MuiMenuItem-root': { height: '1.2rem !important', alignItems: 'center !important' },
          '& .MuiTypography-root': { fontSize: '0.7rem !important', padding: '0.3rem 0.8rem 0rem 0.9rem !important' },
          '& .MuiSvgIcon-root': { fontSize: '1rem !important', verticalAlign: 'middle', fill: 'var(--color-text-secondary)' },
        }}
      >
        <MenuItem onClick={() => handleGenerateTasksEventsReport('self_enroll_portal')} className={classes.menuItem}>
          <ListItemIcon>
            <GroupAddIcon sx={{ fontSize: '0.9rem', paddingLeft: '0.2rem' }} />
          </ListItemIcon>
          Generate tasks & events report
        </MenuItem>
        <MenuItem onClick={() => handleTasksEventsExtract('self_enroll_portal')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Download tasks & events report
        </MenuItem>
        <MenuItem onClick={() => handleInactiveCandidatesReport('self_enroll_portal')} className={classes.menuItem}>
          <ListItemIcon>
            <GetAppIcon sx={{ fontSize: '1rem' }} />
          </ListItemIcon>
          Inactive candidates report
        </MenuItem>
      </NestedMenuItem>
    </Menu>
  )

  const renderMenu = (
    <Menu
      id={menuId}
      open={Boolean(mobileAnchorEl)}
      anchorEl={mobileAnchorEl}
      onClose={handleMobileMenuClose}
      keepMounted
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ top: '6%', left: '1%' }}
    >
      {isPatientDetailsPage() &&
        loggedInUser?.roles?.some((s: string) => ['NAVIGATOR', 'PROVIDER'].includes(s)) &&
        <>
          {getPatientMenu()}
          {!Helper.is_AIDA_OIDC() && <Divider className={classes.patientOperationsDivider} />}
          <MenuItem onClick={onExport} className={classes.menuItem}>
            <ListItemIcon><GetAppIcon fontSize="small" /></ListItemIcon>Export patient
          </MenuItem>
          {patientDetail.is_test_patient &&
            <MenuItem onClick={handleConfirm} className={classes.menuItem}>
              <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>Reset patient
            </MenuItem>
          }
        </>
      }
      {(isOktaEnabled || Helper.isOIDC()) && (
        <>
          {(isPatientDetailsPage() &&
            loggedInUser?.roles?.some((s: string) => ['NAVIGATOR', 'PROVIDER'].includes(s))) &&
            <Divider className={classes.patientOperationsDivider} />
          }
          {Helper.isOIDC()
            ? <OidcLogoutComponent
              history={undefined}
              location={undefined}
              match={undefined}
              handleLogout={handleMobileMenuClose}
            />
            : <OktaLogoutComponent
              history={undefined}
              location={undefined}
              match={undefined}
              handleLogout={handleMobileMenuClose}
            />
          }
        </>
      )}
    </Menu>
  )

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static" style={{ background: currentRole === 'NAVIGATOR' ? '#0047AB' : '#000000' }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <IconButton
              edge='start'
              className={classes.menuButton}
              sx={{ display: { md: 'none' } }}
              color='inherit'
              aria-label='open drawer'
              onClick={() => openDrawerHandler()}
            >
              <MenuIcon />
            </IconButton>
            {showAppTitle && (
              <Link to={''}>
                <img src={atman_logo} alt='Atman Health' className={classes.appLogo} onClick={() => redirectToHome()} />
              </Link>
            )}
            <Box sx={{ width: '100%' }}>
              {(isPatientDetailsPage() && loggedInUser?.roles?.some((s: string) => s === 'NAVIGATOR' || s === 'PROVIDER')) &&
                <MultiProgramInfoHeaderComponent handleClick={{}} />
              }
            </Box>
            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: { md: 'flex' } }}>
              {checkCondition(currentRole,
                <Box>
                  <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                    {isPatientListPage() && !Helper.isAIDA() &&
                      <IconButton
                        onClick={() => { setJobsDialogOpen(true); jobsDialogOpenRef.current = true; startPolling() }}
                        size="small"
                        sx={{ color: '#fff', mr: 1, '&:hover': { color: 'var(--color-primary)' } }}
                        title="Background Jobs"
                      >
                        <Badge badgeContent={jobCount} sx={{ '& .MuiBadge-badge': { backgroundColor: 'var(--color-primary)', color: '#fff' } }}>
                          <WorkOutlineIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    }
                    <KbStatusIndicator />
                    <IconButton
                      onClick={toggleMode}
                      size="small"
                      sx={{ color: '#fff', mr: 1, '&:hover': { color: 'var(--color-primary)' } }}
                      title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                    >
                      {mode === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                    </IconButton>
                    {((isOktaEnabled || Helper.is_AIDA_OIDC()) &&
                      ['/patient-list', '/', '/all-tasks', '/my-tasks'].includes(url.pathname)
                    ) &&
                      <Button
                        variant="text"
                        className={classes.logoutBtn}
                        onMouseOver={handleOperationMenuOpen}
                        endIcon={<KeyboardArrowDownIcon sx={{ marginLeft: '-5px !important', paddingRight: '1rem !important' }} />}
                      >
                        Operations
                      </Button>
                    }
                    <Button variant="text" className={classes.logoutBtn}
                      sx={{ flexShrink: 0 }}
                      onClick={(isOktaEnabled || Helper.is_AIDA_OIDC() ||
                        (!(isOktaEnabled || Helper.is_AIDA_OIDC()) && isPatientDetailsPage())
                      ) ? handleMobileMenuOpen
                        : undefined
                      }
                    >
                      {checkCondition(loggedInUser?.name !== undefined, `${loggedInUser?.name} (${Helper.toCamelCaseRole(currentRole) || '-'})`, '-')}
                      {((isOktaEnabled || Helper.isOIDC()) || (!isOktaEnabled && isPatientDetailsPage())) &&
                        <Badge
                          badgeContent={isPatientDetailsPage() ? scratchPadOpenCount : 0}
                          sx={{ '& .MuiBadge-badge': { backgroundColor: 'var(--color-primary)', color: '#fff' } }}
                        >
                          <MoreVertIcon />
                        </Badge>
                      }
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
            {/* <Box className={classes.sectionMobile} sx={{ display: { md: 'none' } }}>
              <IconButton
                aria-label='show more'
                aria-controls={'primary-menu-mobile'}
                aria-haspopup='true'
                onClick={handleMobileMenuOpen}
                color='inherit'
              >
                <MoreVertIcon />
              </IconButton>
            </Box> */}
          </Toolbar>
        </AppBar>
        {patientDetailStatus === 'refreshing' && (
          <LinearProgress sx={{ height: 2 }} />
        )}
        {(isPatientDetailsPage() &&
          loggedInUser?.roles?.some((s: string) => ['NAVIGATOR', 'PROVIDER'].includes(s))) &&
          <SubHeaderComponent handleCloseOutNote={handleCloseOutNote} />
        }
        {Helper.is_AIDA_OIDC() || environment.okta.isEnabled
          ? <>
            {environment.okta.isEnabled ? renderOperationMenu : renderAIDAOperationMenu}
          </>
          : ''
        }
        {patientDetailStatus !== 'loading' && renderMenu}
      </Box>
      {checkCondition(confirmOpen,
        <ConfirmationDialogComponent
          open={confirmOpen}
          content={`Are you sure you want to reset the patient details?`}
          handleConfirmClose={handleConfirmClose}
          confirmStatus={confirmStatus}
        />)
      }
      {syncOpen &&
        <ConfirmationDialogComponent
          open={syncOpen}
          content={`Are you sure you want to Sync elation patient details?`}
          handleConfirmClose={handleConfirmSyncClose}
          confirmStatus={confirmSyncStatus}
        />
      }
      {sigsCleanup &&
        <ConfirmationDialogComponent
          open={sigsCleanup}
          content={`Are you sure you want to Cleanup elation sigs?`}
          handleConfirmClose={handleConfirmSigsClose}
          confirmStatus={confirmSigsCLeanup}
        />
      }
      {dailyAssignment &&
        <ConfirmationDialogComponent
          open={dailyAssignment}
          content={`Are you sure you want to start assigning tasks?`}
          handleConfirmClose={handleConfirmAssignmentClose}
          confirmStatus={handleAssignTask}
        />
      }
      {isAddPrg &&
        <AddProgramDialogComponent
          open={isAddPrg}
          item={{
            title: 'Add new program',
            label: 'Select Program',
            prgData: getPrgList()
          }}
          handleDialogClose={handlePrgDialogClose}
          handleSubmit={handlePrgDialogSubmit}
        />
      }
      {(isAddPatients || isAddPatientsHH || isAddPatientsCC || isUploadFiles || isAddSpreadsheets || isTxtReport || isTxtReportHH || isTxtReportCC || isImportTasks) &&
        <CcdUploadsComponent
          openUpload={true}
          input={{
            title: isTxtReport
              ? 'Generate SMS report'
              : isTxtReportHH
                ? 'Generate Healthy Heart SMS report'
                : isTxtReportCC
                  ? 'Generate ChamberCardio SMS report'
                  : isAddSpreadsheets
                    ? 'Generate daily task list'
                    : isAddPatients ? 'Add optum patients'
                      : isAddPatientsHH ? 'Add Healthy Heart patients'
                        : isAddPatientsCC ? 'Add ChamberCardio patients'
                          : isImportTasks ? 'Import SF Tasks' : 'CCD elation uploads',
            accept: (isTxtReport || isTxtReportHH || isTxtReportCC)
              ? '.csv'
              : isAddSpreadsheets ? '' : (isAddPatients || isAddPatientsHH || isAddPatientsCC) ? '.xlsx' : isImportTasks ? '.xlsx' : '.xml',
            mrn: isUploadFiles ? patientDetail.mrns[0] : '',
            // Explicit hospital discriminator — HIPAA-critical. Never derive
            // this from the title string inside the dialog; rename-safe.
            hospital: (isAddPatientsCC || isTxtReportCC) ? 'chambercardio' : (isAddPatientsHH || isTxtReportHH) ? 'healthyheart' : 'optum',
          }}
          onUploadClose={handleUploadFilesClose}
        />
      }
      {(isDownloadTaskReport) &&
        <ReportDownloadComponent
          openUpload={isDownloadTaskReport}
          input={{
            title: 'Download daily task report',
          }}
          onUploadClose={handleCloseDownloadTaskReport}
        />
      }
      {(isAddAppointment) &&
        <AddAppointmentsComponent
          openUpload={isAddAppointment}
          input={{
            title: 'Add elation appointments for optum',
            hospital: 'optum',
          }}
          onUploadClose={handleCloseAddAppointment}
        />
      }
      {(isAddAppointmentHH) &&
        <AddAppointmentsComponent
          openUpload={isAddAppointmentHH}
          input={{
            title: 'Add elation appointments for Healthy Heart',
            hospital: 'healthyheart',
          }}
          onUploadClose={handleCloseAddAppointmentHH}
        />
      }
      {isExportPatients && (
        <ExportPatientsDialogComponent
          open={isExportPatients}
          onClose={() => setIsExportPatients(false)}
        />
      )}
      {isNoteOptionsOpen && (
        <GenerateNoteOptionsDialogComponent
          open={isNoteOptionsOpen}
          handleClose={() => setIsNoteOptionsOpen(false)}
          handleGenerate={handleGenerateNoteWithOptions}
        />
      )}
      {confirmNoteOpen && (
        <GeneratedNotesDialogComponent
          open={confirmNoteOpen}
          content={visitNote}
          isCloseoutNote={isCloseoutNote}
          handleConfirmNoteClose={handleConfirmNoteClose}
          submitNote={onWriteNote}
        />
      )}
      {isTranscriptOpen && (
        <Dialog
          open={isTranscriptOpen}
          onClose={() => setIsTranscriptOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Last Transcript</Typography>
              {transcriptData && (
                <Typography variant="caption" color="textSecondary">
                  {transcriptData.call_type_display} - {transcriptData.created_at ? moment(transcriptData.created_at).format('MMM D, YYYY h:mm A') : ''}
                </Typography>
              )}
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            {transcriptLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : transcriptData ? (
              <>
                <Tabs
                  value={transcriptTab}
                  onChange={(_, newValue) => setTranscriptTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab label="Transcript" />
                  <Tab label="Summary" />
                </Tabs>
                {transcriptTab === 0 && (
                  <Box sx={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem', backgroundColor: 'var(--color-paper)', p: 2, borderRadius: 1, border: '1px solid var(--color-border-light)' }}>
                    {transcriptData.transcript?.diarized || transcriptData.transcript?.raw || 'No transcript available'}
                  </Box>
                )}
                {transcriptTab === 1 && (
                  <Box sx={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.85rem', p: 1 }}>
                    {stripThinkingTrace(transcriptData.summary?.text) || 'No summary available'}
                  </Box>
                )}
              </>
            ) : (
              <Typography color="textSecondary">No transcript data available</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsTranscriptOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {isScratchPadOpen && (
        <Dialog
          open={isScratchPadOpen}
          onClose={() => setIsScratchPadOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6">Scratch Pad</Typography>
                <Chip label="from consumer intake" size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
              </Stack>
              {!scratchPadLoading && scratchPadEntries.length > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {scratchPadEntries.length} item{scratchPadEntries.length === 1 ? '' : 's'} flagged for review
                </Typography>
              )}
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            {scratchPadLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : scratchPadEntries.length === 0 ? (
              <Typography color="textSecondary" sx={{ py: 3 }}>
                Nothing flagged — the consumer intake captured no ambiguous medications
                or clinical asides for this patient.
              </Typography>
            ) : (
              <>
                {renderScratchPadGroup(
                  'Medications',
                  scratchPadEntries.filter(
                    (e) => SCRATCH_PAD_CATEGORY_META[e?.category]?.group === 'medication'
                  )
                )}
                {renderScratchPadGroup(
                  'Clinical mentions',
                  scratchPadEntries.filter(
                    (e) => SCRATCH_PAD_CATEGORY_META[e?.category]?.group !== 'medication'
                  )
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsScratchPadOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <BackgroundJobsDialog
        open={jobsDialogOpen}
        onClose={() => { setJobsDialogOpen(false); jobsDialogOpenRef.current = false }}
        activeJobs={activeJobs}
        onRefresh={async () => {
          try {
            const data = await backgroundJobService.getActiveJobs()
            setActiveJobs(data)
          } catch {}
        }}
      />
    </>
  );
};

export default withStyles(stylesheet)(ToolbarComponent);